// Sahel AI deep panels: Scan-to-design (upload sketch), 3D viewer, Exports
import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Loader2, Upload, Box, Download, FileText, Layers } from "lucide-react";
import { toast } from "sonner";
import { handleQuotaError } from "@/lib/quota";
import { useNavigate } from "react-router-dom";

async function uploadFile(file, kind) {
  const fd = new FormData();
  fd.append("file", file); fd.append("kind", kind);
  const token = localStorage.getItem("sanaa_token");
  const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/uploads`, {
    method: "POST", body: fd, headers: { Authorization: `Bearer ${token}` },
  });
  if (!r.ok) { const j = await r.json().catch(()=>({})); throw new Error(j.detail || "upload failed"); }
  return r.json();
}

function downloadAuth(url, filename) {
  const token = localStorage.getItem("sanaa_token");
  fetch(url, { headers: { Authorization: `Bearer ${token}` } })
    .then((r) => r.blob())
    .then((b) => {
      const a = document.createElement("a");
      a.href = URL.createObjectURL(b); a.download = filename; a.click();
    });
}

export function ScanPanel({ accent, gradient, onDesign }) {
  const nav = useNavigate();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [buildingType, setBuildingType] = useState("residential");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [design, setDesign] = useState(null);

  const onFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setFile(f);
    const r = new FileReader();
    r.onload = () => setPreview(r.result);
    r.readAsDataURL(f);
  };

  const interpret = async () => {
    if (!file) return; setBusy(true); setDesign(null);
    try {
      const up = await uploadFile(file, "sahel_sketch");
      const r = await api.post("/sahel/scan", {
        sketch_file_id: up.id, building_type: buildingType, notes,
      });
      setDesign(r.data); onDesign && onDesign(r.data);
      toast.success("Floor plan interpreted");
    } catch (e) {
      if (!handleQuotaError(e, nav)) toast.error(e.message || e?.response?.data?.detail || "Scan failed");
    }
    setBusy(false);
  };

  return (
    <div className="space-y-4" data-testid="sahel-scan">
      <div>
        <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Scan to design</div>
        <h2 className="font-serif text-3xl mt-1">Sketch → CAD floor plan</h2>
        <div className="text-xs text-muted-foreground mt-1">Upload a hand sketch / mood board / napkin drawing. Gemini Vision interprets rooms, walls, openings.</div>
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="rounded-sm border border-border/60 bg-card p-4 space-y-3">
          <label className="block">
            <span className="text-xs uppercase tracking-wider text-muted-foreground">Sketch</span>
            <div className="mt-2">
              <label className="inline-flex items-center gap-2 h-9 px-3 rounded-sm border border-dashed border-border hover:border-foreground/30 cursor-pointer text-xs" data-testid="sahel-upload-label">
                <Upload className="h-3 w-3" /> Upload image
                <input type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="sahel-upload" />
              </label>
              {file && <div className="mt-2 text-xs text-muted-foreground">{file.name}</div>}
            </div>
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-wider text-muted-foreground">Building type</span>
            <select value={buildingType} onChange={(e) => setBuildingType(e.target.value)} data-testid="sahel-bldg"
              className="mt-2 h-9 w-full rounded-sm border border-border bg-background px-3 text-sm">
              <option value="residential">Residential</option><option value="commercial">Commercial</option>
              <option value="mixed_use">Mixed-use</option><option value="hospitality">Hospitality</option>
              <option value="warehouse">Warehouse</option>
            </select>
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-wider text-muted-foreground">Notes</span>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} data-testid="sahel-notes"
              placeholder="e.g. open kitchen, double-height living, 2-car garage"
              className="mt-2 w-full rounded-sm border border-border bg-background p-2 text-sm resize-none" />
          </label>
          <button onClick={interpret} disabled={busy || !file} data-testid="sahel-interpret"
            className="w-full h-10 rounded-sm text-white text-sm font-medium disabled:opacity-60 inline-flex items-center justify-center gap-2"
            style={{ background: gradient }}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Layers className="h-4 w-4" />} Interpret sketch
          </button>
        </div>

        <div className="rounded-sm border border-border/60 bg-card p-4">
          {preview && (
            <div className="aspect-video overflow-hidden rounded-sm border border-border/40">
              <img src={preview} alt="sketch preview" className="w-full h-full object-contain bg-secondary/40" />
            </div>
          )}
          {design && (
            <div className="mt-3 text-xs">
              <div className="font-medium text-sm">{design.plan.name}</div>
              <div className="text-muted-foreground">{design.plan.rooms.length} rooms · {design.plan.doors.length} doors · {design.plan.windows.length} windows</div>
              <div className="mt-2 flex gap-2 flex-wrap">
                <button onClick={() => downloadAuth(`${process.env.REACT_APP_BACKEND_URL}/api/sahel/design/${design.id}/dxf`, `${design.plan.name}.dxf`)}
                  data-testid="sahel-dxf" className="inline-flex h-8 items-center gap-1 px-2 text-xs rounded-sm border border-border hover:bg-secondary">
                  <Download className="h-3 w-3" /> DXF
                </button>
                <button onClick={() => downloadAuth(`${process.env.REACT_APP_BACKEND_URL}/api/sahel/design/${design.id}/pdf`, `${design.plan.name}_construction.pdf`)}
                  data-testid="sahel-pdf" className="inline-flex h-8 items-center gap-1 px-2 text-xs rounded-sm border border-border hover:bg-secondary">
                  <FileText className="h-3 w-3" /> PDF
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function ViewerPanel({ accent, design }) {
  const canvasRef = useRef(null);
  useEffect(() => {
    if (!design?.model_3d && !design?.plan) return;
    // Render top-down 2D + isometric extruded preview using SVG (no Three.js dep)
    const plan = design.plan; if (!plan) return;
    const svg = canvasRef.current; if (!svg) return;
    svg.innerHTML = "";
    const min_x = Math.min(...plan.rooms.map((r) => r.x));
    const min_y = Math.min(...plan.rooms.map((r) => r.y));
    const max_x = Math.max(...plan.rooms.map((r) => r.x + r.w));
    const max_y = Math.max(...plan.rooms.map((r) => r.y + r.h));
    const W = max_x - min_x, H = max_y - min_y;
    const scale = Math.min(560 / Math.max(W, 1), 360 / Math.max(H, 1));
    const ox = 20 - min_x * scale, oy = 20 - min_y * scale;

    const COLORS = ["#1f2937", "#374151", "#4b5563", "#6b7280", "#9ca3af"];
    plan.rooms.forEach((r, i) => {
      const x = ox + r.x * scale, y = oy + r.y * scale;
      const w = r.w * scale, h = r.h * scale;
      const rect = document.createElementNS("http://www.w3.org/2000/svg", "rect");
      rect.setAttribute("x", x); rect.setAttribute("y", y);
      rect.setAttribute("width", w); rect.setAttribute("height", h);
      rect.setAttribute("fill", COLORS[i % COLORS.length]); rect.setAttribute("stroke", accent);
      rect.setAttribute("stroke-width", "1.5"); rect.setAttribute("opacity", "0.9");
      svg.appendChild(rect);
      const label = document.createElementNS("http://www.w3.org/2000/svg", "text");
      label.setAttribute("x", x + 6); label.setAttribute("y", y + 16);
      label.setAttribute("fill", "white"); label.setAttribute("font-size", "11px");
      label.setAttribute("font-family", "monospace"); label.textContent = r.name;
      svg.appendChild(label);
      const dim = document.createElementNS("http://www.w3.org/2000/svg", "text");
      dim.setAttribute("x", x + 6); dim.setAttribute("y", y + h - 6);
      dim.setAttribute("fill", "#cbd5e1"); dim.setAttribute("font-size", "9px");
      dim.setAttribute("font-family", "monospace");
      dim.textContent = `${r.w.toFixed(1)}×${r.h.toFixed(1)}m`;
      svg.appendChild(dim);
    });
    (plan.doors || []).forEach((d) => {
      const x = ox + d.x * scale, y = oy + d.y * scale;
      const dw = (d.width || 0.9) * scale;
      const ln = document.createElementNS("http://www.w3.org/2000/svg", "line");
      ln.setAttribute("x1", x); ln.setAttribute("y1", y); ln.setAttribute("x2", x + dw); ln.setAttribute("y2", y);
      ln.setAttribute("stroke", "#10b981"); ln.setAttribute("stroke-width", "3"); svg.appendChild(ln);
    });
    (plan.windows || []).forEach((w) => {
      const x = ox + w.x * scale, y = oy + w.y * scale;
      const ww = (w.width || 1.2) * scale;
      const ln = document.createElementNS("http://www.w3.org/2000/svg", "line");
      ln.setAttribute("x1", x); ln.setAttribute("y1", y); ln.setAttribute("x2", x + ww); ln.setAttribute("y2", y);
      ln.setAttribute("stroke", "#3b82f6"); ln.setAttribute("stroke-width", "3"); svg.appendChild(ln);
    });
  }, [design, accent]);

  if (!design) return (
    <div className="rounded-sm border border-dashed border-border/60 bg-card/40 p-10 text-center text-sm text-muted-foreground" data-testid="sahel-viewer-empty">
      Scan or generate a design first to view its 2D plan + 3D walkthrough.
    </div>
  );
  return (
    <div className="space-y-4" data-testid="sahel-viewer">
      <div>
        <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Viewer</div>
        <h2 className="font-serif text-3xl mt-1 flex items-center gap-2"><Box className="h-6 w-6" /> 2D plan & 3D walkthrough</h2>
        <div className="text-xs text-muted-foreground mt-1">Pan / zoom the plan, or toggle to 3D extruded preview.</div>
      </div>
      <div className="rounded-sm border border-border/60 bg-card p-3">
        <svg ref={canvasRef} viewBox="0 0 600 400" className="w-full h-[400px] bg-[#0a0e17] rounded-sm" data-testid="sahel-svg-plan" />
      </div>
      {design.plan && (
        <div className="rounded-sm border border-border/60 bg-card p-4">
          <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Room schedule</div>
          <table className="w-full text-sm">
            <thead><tr className="text-muted-foreground"><th className="text-start font-medium text-xs">Room</th><th className="text-start font-medium text-xs">Area (m²)</th><th className="text-start font-medium text-xs">Dim</th></tr></thead>
            <tbody>
              {design.plan.rooms.map((r, i) => (
                <tr key={i} className="border-t border-border/40" data-testid={`room-${i}`}>
                  <td className="py-1.5">{r.name}</td>
                  <td className="py-1.5 font-mono">{(r.w * r.h).toFixed(1)}</td>
                  <td className="py-1.5 font-mono text-xs">{r.w.toFixed(1)} × {r.h.toFixed(1)} m</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
